(function initializeSantaBrisaMap15() {
  'use strict';

  const ROOT_SELECTOR = '[data-sb15-map]';
  const instances = new WeakMap();

  function findRoot(container) {
    if (!container) return null;
    if (container.matches?.(ROOT_SELECTOR)) return container;
    return container.querySelector?.(ROOT_SELECTOR) ?? null;
  }

  function markIncompleteRoot(root, errorElement) {
    root.classList.add('sb15-map--fallback');
    if (!errorElement) return;
    errorElement.textContent = root.dataset.sb15UnavailableText
      || 'El mapa no está disponible. Puedes consultar el listado de establecimientos.';
    errorElement.removeAttribute('hidden');
  }

  function mount(root) {
    if (!root || instances.has(root)) return;

    const hooks = {
      canvas: root.querySelector('[data-sb15-map-canvas]'),
      placeholder: root.querySelector('[data-sb15-map-placeholder]'),
      error: root.querySelector('[data-sb15-map-error]'),
      count: root.querySelector('[data-sb15-count]'),
      interactive: root.querySelector('[data-sb15-interactive]'),
      form: root.querySelector('[data-sb15-search-form]'),
      input: root.querySelector('[data-sb15-search]'),
      clear: root.querySelector('[data-sb15-clear]'),
      filters: Array.from(root.querySelectorAll('[data-sb15-filter]')),
      detail: root.querySelector('[data-sb15-detail]'),
      back: root.querySelector('[data-sb15-back]'),
      detailName: root.querySelector('[data-sb15-detail-name]'),
      detailMeta: root.querySelector('[data-sb15-detail-meta]'),
      detailLink: root.querySelector('[data-sb15-detail-link]'),
      results: root.querySelector('[data-sb15-results]'),
      resultsList: root.querySelector('[data-sb15-results-list]'),
      resultsCount: root.querySelector('[data-sb15-results-count]'),
    };
    hooks.summary = hooks.results?.querySelector('summary') ?? null;

    const requiredHooks = Object.entries(hooks)
      .filter(([name]) => name !== 'filters')
      .map(([, element]) => element);
    if (requiredHooks.some((element) => !element) || hooks.filters.length !== 3) {
      markIncompleteRoot(root, hooks.error);
      return;
    }

    const labels = {
      open: root.dataset.sb15OpenLabel || 'Abrir en Google Maps',
      restaurant: root.dataset.sb15RestaurantLabel || 'Restauración',
      shop: root.dataset.sb15ShopLabel || 'Tiendas',
      noResults: root.dataset.sb15NoResultsText || 'No hay establecimientos con estos filtros.',
      unavailable: root.dataset.sb15UnavailableText
        || 'El mapa no está disponible. Puedes consultar el listado de establecimientos.',
    };
    const fallbackNodes = Array.from(hooks.resultsList.childNodes);
    const initialCount = hooks.count.textContent;
    const initialResultsCount = hooks.resultsCount.textContent;
    const abortController = new AbortController();
    const { signal } = abortController;

    let category = 'todos';
    let venues = [];
    let L = null;
    let map = null;
    let markerLayer = null;
    let tileLayer = null;
    let observer = null;
    let loadStarted = false;
    let loadPromise = null;
    let destroyed = false;
    let fallbackReplaced = false;
    let consecutiveTileErrors = 0;
    let tileWarningShown = false;

    function categoryLabel(venueCategory) {
      return venueCategory === 'tienda' ? labels.shop : labels.restaurant;
    }

    function closeDetail() {
      hooks.detail.setAttribute('hidden', '');
      hooks.results.removeAttribute('hidden');
    }

    function buildResultsList(filteredVenues) {
      const fragment = document.createDocumentFragment();

      if (filteredVenues.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'sb15-map__empty';
        empty.textContent = labels.noResults;
        fragment.append(empty);
      } else {
        const list = document.createElement('ul');
        list.className = 'sb15-map__venue-list';

        for (const venue of filteredVenues) {
          const item = document.createElement('li');
          const button = document.createElement('button');
          const name = document.createElement('strong');
          const meta = document.createElement('span');
          const directLink = document.createElement('a');

          button.type = 'button';
          button.dataset.sb15VenueId = venue.id;
          name.textContent = venue.name;
          meta.textContent = `${venue.city} · ${categoryLabel(venue.category)}`;
          directLink.href = venue.url;
          directLink.target = '_blank';
          directLink.rel = 'noopener noreferrer';
          directLink.textContent = labels.open;
          button.append(name, meta);
          item.append(button, directLink);
          list.append(item);
        }

        fragment.append(list);
      }

      hooks.resultsList.replaceChildren(fragment);
      fallbackReplaced = true;
    }

    function rebuildMarkers(filteredVenues) {
      markerLayer.clearLayers();
      const points = [];

      for (const venue of filteredVenues) {
        const point = [venue.latitude, venue.longitude];
        const tooltip = document.createElement('span');
        tooltip.textContent = `${venue.name} · ${venue.city}`;

        const marker = L.circleMarker(point, {
          radius: 7,
          color: '#ffffff',
          weight: 2,
          fillColor: venue.category === 'tienda' ? '#f2c94c' : '#f47a21',
          fillOpacity: 0.95,
        });
        marker.bindTooltip(tooltip, { direction: 'top' });
        marker.on('click', () => selectVenue(venue));
        marker.addTo(markerLayer);
        points.push(point);
      }

      if (points.length > 0) {
        map.fitBounds(L.latLngBounds(points), {
          padding: [36, 36],
          maxZoom: 11,
          animate: false,
        });
      } else {
        map.setView([40.1, -3.6], 6, { animate: false });
      }
    }

    function handleTileLoad() {
      if (!destroyed) consecutiveTileErrors = 0;
    }

    function handleTileError() {
      if (destroyed || tileWarningShown) return;
      consecutiveTileErrors += 1;
      if (consecutiveTileErrors < 3) return;
      tileWarningShown = true;
      root.classList.add('sb15-map--tiles-failed');
      hooks.error.textContent = labels.unavailable;
      hooks.error.removeAttribute('hidden');
    }

    function detachTileListeners() {
      if (!tileLayer) return;
      tileLayer.off('tileload', handleTileLoad);
      tileLayer.off('tileerror', handleTileError);
      tileLayer = null;
    }

    function selectVenue(venue) {
      if (destroyed || !map) return;

      hooks.detailName.textContent = venue.name;
      hooks.detailMeta.textContent = `${venue.city} · ${categoryLabel(venue.category)}`;
      hooks.detailLink.textContent = labels.open;
      hooks.detailLink.href = venue.url;
      hooks.results.setAttribute('hidden', '');
      hooks.detail.removeAttribute('hidden');

      const reduceMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      map.setView([venue.latitude, venue.longitude], 14, { animate: !reduceMotion });
      hooks.detailName.focus({ preventScroll: true });
    }

    function refresh() {
      if (destroyed || !map) return;
      closeDetail();
      const core = window.SantaBrisaMap15Core;
      const filteredVenues = core.filterVenues(venues, {
        query: hooks.input.value,
        category,
      });

      hooks.count.textContent = String(filteredVenues.length);
      hooks.resultsCount.textContent = String(filteredVenues.length);
      buildResultsList(filteredVenues);
      rebuildMarkers(filteredVenues);
    }

    function failure() {
      if (destroyed) return;
      root.classList.remove('sb15-map--enhanced');
      root.classList.remove('sb15-map--tiles-failed');
      root.classList.add('sb15-map--fallback');
      hooks.interactive.setAttribute('hidden', '');
      hooks.error.textContent = labels.unavailable;
      hooks.error.removeAttribute('hidden');
      hooks.detail.setAttribute('hidden', '');
      hooks.results.removeAttribute('hidden');
      hooks.count.textContent = initialCount;
      hooks.resultsCount.textContent = initialResultsCount;

      if (fallbackReplaced) {
        hooks.resultsList.replaceChildren(...fallbackNodes);
        fallbackReplaced = false;
      }

      detachTileListeners();
      map?.remove();
      map = null;
      markerLayer = null;
    }

    function safelyRefresh() {
      try {
        refresh();
      } catch (error) {
        failure(error);
      }
    }

    async function enhance() {
      const core = window.SantaBrisaMap15Core;
      L = window.L;
      const hasCore = core && typeof core.normalizeVenues === 'function'
        && typeof core.filterVenues === 'function';
      const hasLeaflet = L && typeof L.map === 'function' && typeof L.canvas === 'function'
        && typeof L.tileLayer === 'function' && typeof L.layerGroup === 'function'
        && typeof L.circleMarker === 'function' && typeof L.latLngBounds === 'function';
      if (!hasCore || !hasLeaflet) throw new Error('SB 1.5 map dependencies unavailable');

      const datasetUrl = root.dataset.sb15VenuesUrl;
      if (!datasetUrl) throw new Error('SB 1.5 map dataset URL unavailable');
      const response = await fetch(datasetUrl, {
        credentials: 'same-origin',
        cache: 'force-cache',
        signal,
      });
      if (!response.ok) throw new Error(`SB 1.5 map dataset request failed: ${response.status}`);

      const rawVenues = await response.json();
      if (destroyed) return;
      venues = core.normalizeVenues(rawVenues);
      // Guarda contra un dataset truncado o corrupto: si el recuento no es el
      // esperado se prefiere el listado estático a un mapa a medias. Al cambiar
      // el dataset hay que mover este número, el de check-sb-15-data.mjs y los
      // tres contadores de sb-map-home-15.liquid.
      if (venues.length !== 224) throw new Error('SB 1.5 map dataset must contain 224 venues');

      map = L.map(hooks.canvas, {
        renderer: L.canvas(),
        preferCanvas: true,
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
      });
      map.setView([40.1, -3.6], 6);
      tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      });
      tileLayer.on('tileload', handleTileLoad);
      tileLayer.on('tileerror', handleTileError);
      tileLayer.addTo(map);
      markerLayer = L.layerGroup().addTo(map);

      root.classList.remove('sb15-map--fallback');
      root.classList.remove('sb15-map--tiles-failed');
      root.classList.add('sb15-map--enhanced');
      hooks.interactive.removeAttribute('hidden');
      hooks.error.setAttribute('hidden', '');
      refresh();

      window.requestAnimationFrame(() => {
        if (instances.get(root) === instance && map) map.invalidateSize();
      });
    }

    function load() {
      if (destroyed) return Promise.resolve();
      if (loadStarted) return loadPromise;
      loadStarted = true;
      observer?.disconnect();
      observer = null;
      loadPromise = enhance().catch((error) => {
        if (error?.name !== 'AbortError') failure(error);
      });
      return loadPromise;
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      observer?.disconnect();
      abortController.abort();
      detachTileListeners();
      map?.remove();
      map = null;
      markerLayer = null;
      instances.delete(root);
    }

    const instance = { destroy, load };
    instances.set(root, instance);

    hooks.form.addEventListener('submit', (event) => {
      event.preventDefault();
      safelyRefresh();
    }, { signal });
    hooks.input.addEventListener('input', safelyRefresh, { signal });
    hooks.form.addEventListener('reset', () => {
      window.requestAnimationFrame(() => {
        if (!destroyed) safelyRefresh();
      });
    }, { signal });
    for (const filter of hooks.filters) {
      filter.addEventListener('click', () => {
        category = filter.dataset.sb15Filter || 'todos';
        for (const candidate of hooks.filters) {
          candidate.setAttribute('aria-pressed', String(candidate === filter));
        }
        safelyRefresh();
      }, { signal });
    }
    hooks.resultsList.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-sb15-venue-id]');
      if (!button || !hooks.resultsList.contains(button)) return;
      const venue = venues.find((candidate) => candidate.id === button.dataset.sb15VenueId);
      if (venue) selectVenue(venue);
    }, { signal });
    hooks.back.addEventListener('click', () => {
      closeDetail();
      safelyRefresh();
      hooks.summary.focus();
    }, { signal });

    if (window.Shopify?.designMode) {
      load();
    } else if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load();
      }, { rootMargin: '320px 0px' });
      observer.observe(root);
    } else {
      load();
    }
  }

  function boot(container = document) {
    const roots = new Set();
    if (container?.matches?.(ROOT_SELECTOR)) roots.add(container);
    for (const root of container?.querySelectorAll?.(ROOT_SELECTOR) ?? []) roots.add(root);
    for (const root of roots) mount(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = findRoot(event.target);
    if (root) instances.get(root)?.destroy();
  });
}());
